import type { DashboardWidget, DashboardWidgetType } from "@gjwtrack/domain";
import { Plus, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useClient } from "../../lib/client.ts";
import { Button, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel } from "../../ui/ring.ts";
import { SimpleSelect } from "../../ui/SimpleSelect.tsx";
import { uuid } from "@gjwtrack/client";

export const WIDGET_TYPES: { type: DashboardWidgetType; label: string; hint: string; w: number; h: number }[] = [
  { type: "issues", label: "Issue List", hint: "Issues matching a query, live", w: 6, h: 5 },
  { type: "counters", label: "Issue Counts", hint: "One number per query", w: 4, h: 2 },
  { type: "distribution", label: "Issues per Project / Field", hint: "Bars per project or field value", w: 4, h: 4 },
  { type: "notes", label: "Notes", hint: "Markdown text", w: 4, h: 3 },
];

export function newWidget(type: DashboardWidgetType, at: { x: number; y: number; w: number; h: number }): DashboardWidget {
  const id = uuid().slice(0, 8);
  switch (type) {
    case "issues":
      return { id, type, ...at, config: { query: "#Unresolved sort by: updated", limit: 15 } };
    case "notes":
      return { id, type, ...at, config: { markdown: "" } };
    case "counters":
      return { id, type, ...at, config: { items: [{ label: "Unresolved", query: "#Unresolved" }] } };
    case "distribution":
      return { id, type, ...at, config: { query: "#Unresolved", by: "project", limit: 10 } };
  }
}

const LIMITS = [5, 10, 15, 20, 30, 50].map((n) => ({ value: String(n), label: `${n} issues` }));
const BAR_LIMITS = [5, 8, 10, 15, 20].map((n) => ({ value: String(n), label: `${n} bars` }));

/** Per-type settings form (Ring Dialog). Returns the whole widget with its config replaced. */
export function WidgetSettingsDialog({ widget, isNew, onSave, onClose }: { widget: DashboardWidget; isNew: boolean; onSave: (w: DashboardWidget) => void; onClose: () => void }) {
  const schema = useClient((s) => s.schema);
  const [title, setTitle] = useState(widget.config.title ?? "");
  const [query, setQuery] = useState("query" in widget.config ? widget.config.query : "");
  const [limit, setLimit] = useState(String("limit" in widget.config ? widget.config.limit : 10));
  const [markdown, setMarkdown] = useState(widget.type === "notes" ? widget.config.markdown : "");
  const [by, setBy] = useState(widget.type === "distribution" ? widget.config.by : "project");
  const [items, setItems] = useState(widget.type === "counters" ? widget.config.items.map((i) => ({ ...i })) : []);
  const [error, setError] = useState<string | null>(null);
  const label = WIDGET_TYPES.find((t) => t.type === widget.type)?.label ?? widget.type;
  const byOptions = [{ value: "project", label: "Project" }, ...(schema?.data.fields ?? []).filter((f) => f.kind !== "text").map((f) => ({ value: f.name, label: f.name }))];

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const t = title.trim() ? { title: title.trim() } : {};
    switch (widget.type) {
      case "issues":
        onSave({ ...widget, config: { ...t, query: query.trim(), limit: Number(limit) } });
        return;
      case "notes":
        onSave({ ...widget, config: { ...t, markdown } });
        return;
      case "distribution":
        onSave({ ...widget, config: { ...t, query: query.trim(), by, limit: Number(limit) } });
        return;
      case "counters": {
        const clean = items.map((i) => ({ label: i.label.trim(), query: i.query.trim() })).filter((i) => i.label);
        if (clean.length === 0) {
          setError("Add at least one counter with a label.");
          return;
        }
        onSave({ ...widget, config: { ...t, items: clean } });
      }
    }
  };

  return (
    <Dialog show label={`${label} settings`} onCloseAttempt={onClose} showCloseButton trapFocus autoFocusFirst={false} contentClassName="widget-settings">
      <DialogHeader>{isNew ? `New ${label} widget` : `${label} settings`}</DialogHeader>
      <form onSubmit={submit}>
        <DialogContent>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">Title</span>
              <Input size={InputSize.FULL} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="(default)" autoFocus />
            </label>
            {(widget.type === "issues" || widget.type === "distribution") && (
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted">Query</span>
                <Input size={InputSize.FULL} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="project: X #Unresolved sort by: updated" className="font-mono" />
              </label>
            )}
            {widget.type === "issues" && (
              <label className="flex items-center gap-2">
                <span className="text-xs text-muted">Show</span>
                <SimpleSelect value={limit} options={LIMITS} onChange={setLimit} />
              </label>
            )}
            {widget.type === "distribution" && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <span className="text-xs text-muted">Per</span>
                  <SimpleSelect value={by} options={byOptions} onChange={setBy} />
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-xs text-muted">Show</span>
                  <SimpleSelect value={limit} options={BAR_LIMITS} onChange={setLimit} />
                </label>
              </div>
            )}
            {widget.type === "notes" && (
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted">Markdown</span>
                <textarea className="field min-h-48 w-full resize-y font-mono text-[13px]" value={markdown} onChange={(e) => setMarkdown(e.target.value)} placeholder="Anything: links, lists, images, DRIVE-12 references…" />
              </label>
            )}
            {widget.type === "counters" && (
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-[10rem_1fr_1.5rem] gap-2 text-xs text-muted">
                  <span>Label</span>
                  <span>Query</span>
                  <span />
                </div>
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[10rem_1fr_1.5rem] items-center gap-2">
                    <Input size={InputSize.FULL} value={it.label} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="Label" />
                    <Input size={InputSize.FULL} value={it.query} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, query: e.target.value } : x)))} placeholder="#Unresolved" className="font-mono" />
                    <button type="button" className="icon-btn" title="Remove" onClick={() => setItems(items.filter((_, j) => j !== i))} disabled={items.length <= 1}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {items.length < 12 && (
                  <Button type="button" icon={Plus} onClick={() => setItems([...items, { label: "", query: "" }])} className="self-start">
                    Add counter
                  </Button>
                )}
              </div>
            )}
            {error && <div className="text-danger">{error}</div>}
          </div>
        </DialogContent>
        <Panel>
          <Button primary type="submit">
            {isNew ? "Add" : "Save"}
          </Button>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
        </Panel>
      </form>
    </Dialog>
  );
}
